// Night mode (plan §5, ask #6). Two orthogonal axes, like lichess/chess.com:
// night mode re-tints only the chrome tokens (--bg/--panel/--ink/--muted/--line
// in `style.css`); the board keeps its own square colours (see `boardTheme.ts`).
//
// "System" removes the attribute so the CSS `prefers-color-scheme` media query
// drives; "Light"/"Dark" stamp `:root[data-theme]`, which wins on specificity in
// both directions. A no-flash inline script in `index.html` applies the stored
// choice before first paint; this module owns runtime changes and keeps the nav
// button + Settings <select> in sync via a `themechange` event.

import { t } from "./i18n/index.ts";

export type Theme = "system" | "light" | "dark";

const STORAGE_KEY = "chess:theme";
const CHANGE_EVENT = "themechange";
const ORDER: Theme[] = ["system", "light", "dark"];

// Icons are static; labels are resolved via t() at render time (module load runs
// before initLang(), so we must not bake language into a top-level const).
const ICON: Record<Theme, string> = { system: "🖥", light: "☀️", dark: "🌙" };
const LABEL_KEY: Record<Theme, string> = {
  system: "theme.systemLabel",
  light: "theme.lightLabel",
  dark: "theme.darkLabel",
};

/** The stored appearance choice (defaults to System). */
export function getTheme(): Theme {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* disabled storage → System */
  }
  return stored === "light" || stored === "dark" ? stored : "system";
}

/** Stamp a theme onto `<html>` (System = remove the attribute). */
function stamp(theme: Theme): void {
  if (theme === "system") document.documentElement.removeAttribute("data-theme");
  else document.documentElement.setAttribute("data-theme", theme);
}

/** Persist + apply an appearance choice and notify listeners. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* non-fatal */
  }
  stamp(theme);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: theme }));
}

/** Re-apply the stored choice at boot (the no-flash script already stamped it,
 *  but this keeps the DOM correct if that script was skipped). */
export function initTheme(): void {
  stamp(getTheme());
}

/** A cycling System → Light → Dark icon button for the top nav (mirrors mute). */
export function themeButton(): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "btn ghost icon theme-toggle";
  const paint = () => {
    const cur = getTheme();
    const label = t(LABEL_KEY[cur]);
    b.textContent = ICON[cur];
    b.setAttribute("aria-label", label);
    b.title = label;
  };
  paint();
  b.addEventListener("click", () => {
    const next = ORDER[(ORDER.indexOf(getTheme()) + 1) % ORDER.length];
    setTheme(next);
  });
  // Keep in sync when another control (Settings <select>) changes the theme.
  window.addEventListener(CHANGE_EVENT, paint);
  return b;
}

/** A native <select> for Settings (System / Light / Dark). */
export function themeSelect(): HTMLElement {
  const field = document.createElement("label");
  field.className = "field";
  field.innerHTML =
    `<span>${t("theme.appearance")}</span>` +
    `<select name="theme">` +
    ORDER.map(
      (opt) => `<option value="${opt}">${t("theme." + opt)}</option>`,
    ).join("") +
    `</select>`;
  const select = field.querySelector("select")!;
  select.value = getTheme();
  select.addEventListener("change", () => setTheme(select.value as Theme));
  window.addEventListener(CHANGE_EVENT, () => (select.value = getTheme()));
  return field;
}
